/**
 * 세부 충돌 칸 — 벽을 48px 타일보다 잘게 칠할 수 있게 한다.
 *
 * ## 왜
 * 배경이 통짜 일러스트인 존에서, 한 칸(48px) 안에 **길과 벽이 같이 든 칸**이 21~51% 다
 * (실측, docs/build/20). 전부 아니면 전무로 정해야 하니 길이 잘리거나 건물이 뚫린다.
 * 나누기를 3×3 으로만 올려도 그 비율이 로비 33→20%, 경주 46→30% 로 떨어진다.
 *
 * ## 불변식 4 를 지킨다
 * **48px 타일 그리드는 그대로다.** 렌더·배치·스냅은 전부 48px. 여기서 잘게 나누는 것은
 * **충돌 판정뿐**이다. 타일 크기를 바꾸는 게 아니라 타일 안을 나눈다.
 *
 * ## 저장 방식
 * - `collision_div`: 1|2|3|4 — 타일 한 변을 몇 조각으로 나눌지. 없으면 1(지금까지의 동작).
 * - `collision_fine`: 길이 `width*height` 의 정수 배열. 각 값은 타일 하나의 **조각 비트마스크**
 *   (비트 index = `sy*div + sx`). div=3 이면 0~511.
 *
 * 타일당 정수 하나라 한 칸을 고쳐도 디프가 그 자리에만 난다(base64 한 덩어리로 두면
 * 한 칸만 바꿔도 전체 줄이 바뀌어 리뷰가 불가능해진다).
 *
 * 기존 `collision` 타일레이어는 **함께 유지**한다 — Tiled 로 열었을 때와, 세부 데이터를
 * 모르는 옛 코드에서 벽이 통째로 사라지지 않도록. "조각이 하나라도 막혔으면 1".
 */
import type { TiledMap } from "./tiledEdit";

/** 고를 수 있는 나누기. 4×4(12px)보다 잘게는 이득이 거의 없다(실측: 16→12px 개선 2~3%p). */
export const COLLISION_DIVS = [1, 2, 3, 4] as const;
export type CollisionDiv = (typeof COLLISION_DIVS)[number];

export const DIV_LABEL: Record<CollisionDiv, string> = {
  1: "안 나눔 (48px)",
  2: "4조각 (24px)",
  3: "9조각 (16px)",
  4: "16조각 (12px)",
};

export function isCollisionDiv(v: unknown): v is CollisionDiv {
  return (COLLISION_DIVS as readonly number[]).includes(v as number);
}

/** 이 맵의 나누기. 없거나 이상하면 1(타일 통째). */
export function collisionDiv(map: TiledMap): CollisionDiv {
  const v = (map as { collision_div?: unknown }).collision_div;
  return isCollisionDiv(v) ? v : 1;
}

/** 타일당 조각 비트마스크 배열. 없으면 `collision` 레이어에서 만든다(전부 막힘/전부 열림). */
export function fineData(map: TiledMap): number[] {
  const n = map.width * map.height;
  const raw = (map as { collision_fine?: unknown }).collision_fine;
  if (Array.isArray(raw) && raw.length === n) return raw.map((v) => (typeof v === "number" && v >= 0 ? v : 0));
  const div = collisionDiv(map);
  const full = (1 << (div * div)) - 1;
  const coarse = coarseLayerData(map);
  return Array.from({ length: n }, (_, i) => ((coarse[i] ?? 0) !== 0 ? full : 0));
}

function coarseLayerData(map: TiledMap): number[] {
  const layer = map.layers.find((l) => l.type === "tilelayer" && l.name === "collision");
  return Array.isArray(layer?.data) ? (layer.data as number[]) : [];
}

/** 조각 좌표(맵 전체 기준)로 막혔는지. `sx`,`sy` 는 0..width*div-1. */
export function isBlockedSub(map: TiledMap, sx: number, sy: number): boolean {
  const div = collisionDiv(map);
  const gx = Math.floor(sx / div);
  const gy = Math.floor(sy / div);
  if (gx < 0 || gy < 0 || gx >= map.width || gy >= map.height) return true; // 맵 밖은 막힘
  const mask = fineData(map)[gy * map.width + gx] ?? 0;
  return (mask & (1 << ((sy % div) * div + (sx % div)))) !== 0;
}

/** 조각 하나 토글. 편집기 붓이 쓴다. */
export function toggleCollisionSub(map: TiledMap, sx: number, sy: number): TiledMap {
  return paintCollisionSub(map, sx, sy, !isBlockedSub(map, sx, sy));
}

/** 조각 하나를 막힘/열림으로 지정(드래그 칠하기 — 지나간 칸이 토글로 깜빡이지 않게). */
export function paintCollisionSub(map: TiledMap, sx: number, sy: number, blocked: boolean): TiledMap {
  const div = collisionDiv(map);
  const gx = Math.floor(sx / div);
  const gy = Math.floor(sy / div);
  if (gx < 0 || gy < 0 || gx >= map.width || gy >= map.height) return map;
  const idx = gy * map.width + gx;
  const bit = 1 << ((sy % div) * div + (sx % div));
  const fine = fineData(map);
  const next = blocked ? (fine[idx] ?? 0) | bit : (fine[idx] ?? 0) & ~bit;
  if (next === fine[idx]) return map;
  fine[idx] = next;
  return withFine(map, div, fine);
}

/**
 * 나누기 변경. 기존에 칠한 것을 **최대한 살려서** 다시 표본한다 —
 * 나누기를 바꿀 때마다 벽이 사라지면 운영자가 다시 칠해야 한다.
 * **면적 과반**으로 표본한다 — 새 조각이 덮는 넓이의 절반 이상이 막혀 있었으면 막힘.
 */
export function setCollisionDiv(map: TiledMap, div: CollisionDiv): TiledMap {
  const old = collisionDiv(map);
  if (old === div) return map;
  const oldFine = fineData(map);
  const next: number[] = [];
  for (let gy = 0; gy < map.height; gy++) {
    for (let gx = 0; gx < map.width; gx++) {
      const om = oldFine[gy * map.width + gx] ?? 0;
      let mask = 0;
      for (let sy = 0; sy < div; sy++) {
        for (let sx = 0; sx < div; sx++) {
          // 새 조각이 덮는 넓이 중 **막힌 넓이가 절반 이상**이면 막힘.
          // 중심점 하나만 보면 잘게 칠한 벽이 통째로 사라진다(4→2 로 줄일 때 실제로 그랬다).
          let blockedArea = 0;
          let total = 0;
          for (let oy = 0; oy < old; oy++) {
            const overlapY = Math.max(0, Math.min((sy + 1) / div, (oy + 1) / old) - Math.max(sy / div, oy / old));
            if (overlapY <= 0) continue;
            for (let ox = 0; ox < old; ox++) {
              const overlapX = Math.max(0, Math.min((sx + 1) / div, (ox + 1) / old) - Math.max(sx / div, ox / old));
              if (overlapX <= 0) continue;
              const a = overlapX * overlapY;
              total += a;
              if ((om & (1 << (oy * old + ox))) !== 0) blockedArea += a;
            }
          }
          if (total > 0 && blockedArea / total >= 0.5 - 1e-9) mask |= 1 << (sy * div + sx);
        }
      }
      next.push(mask);
    }
  }
  return withFine(map, div, next);
}

/** 세부 데이터를 맵에 얹고, 옛 `collision` 레이어도 함께 맞춘다(조각이 하나라도 막혔으면 1). */
function withFine(map: TiledMap, div: CollisionDiv, fine: number[]): TiledMap {
  return {
    ...map,
    collision_div: div,
    collision_fine: fine,
    layers: map.layers.map((l) =>
      l.type === "tilelayer" && l.name === "collision" && Array.isArray(l.data)
        ? { ...l, data: fine.map((m) => (m !== 0 ? 1 : 0)) }
        : l,
    ),
  };
}

export interface BlockRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * 막힌 조각들을 **직사각형 몇 개로 병합**한다(greedy). 조각 격자만 받는 **알맹이**다.
 *
 * 왜 알맹이를 따로 두나: 같은 병합을 **두 곳**이 쓴다 —
 * 관리자 편집기(`TiledMap`)와 아이 화면의 월드(`ParsedMap`). 자료 모양이 달라서
 * 예전에는 **같은 알고리즘을 두 벌 적어 두었다**(WorldScene 안에 복사본이 있었고,
 * 주석에 「shared 와 같은 알고리즘」이라고 적혀 있었다).
 *
 * 두 벌이면 한쪽만 고쳐도 아무 에러가 안 난다 — 관리자에서는 벽이 맞는데
 * 아이 화면에서만 통과되는, 이 저장소가 가장 자주 당한 종류의 사고가 된다.
 *
 * @param blocked 조각 격자. `blocked(x, y)` 가 참이면 막힌 조각이다.
 * @param W 가로 조각 수 · @param H 세로 조각 수 · @param cell 조각 한 변의 픽셀
 */
export function mergeBlockedGrid(
  blocked: (x: number, y: number) => boolean,
  W: number,
  H: number,
  cell: number,
): BlockRect[] {
  const taken = new Uint8Array(W * H);
  const out: BlockRect[] = [];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (taken[y * W + x] || !blocked(x, y)) continue;
      // 가로로 최대한 이어 붙이고
      let w = 1;
      while (x + w < W && !taken[y * W + x + w] && blocked(x + w, y)) w++;
      // 같은 폭이 계속되는 동안 세로로도 이어 붙인다
      let h = 1;
      grow: while (y + h < H) {
        for (let i = 0; i < w; i++) {
          if (taken[(y + h) * W + x + i] || !blocked(x + i, y + h)) break grow;
        }
        h++;
      }
      for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) taken[(y + j) * W + x + i] = 1;
      out.push({ x: x * cell, y: y * cell, w: w * cell, h: h * cell });
    }
  }
  return out;
}

/**
 * 편집기 지도(`TiledMap`)의 막힌 조각 → 직사각형.
 * 왜 병합하나: 경주존을 3×3 으로 나누면 막힌 조각이 1,800개가 넘는다.
 * 물리 엔진에 정적 바디를 그만큼 만들면 느려진다.
 */
export function mergedBlockedRects(map: TiledMap): BlockRect[] {
  const div = collisionDiv(map);
  return mergeBlockedGrid(
    (x, y) => isBlockedSub(map, x, y),
    map.width * div,
    map.height * div,
    map.tilewidth / div,
  );
}
